import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

function normalizeRole(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null) // full profile from public.users
  const [loading, setLoading] = useState(true)
  const [profileError, setProfileError] = useState('')
  const profileReqRef = useRef(0)

  useEffect(() => {
    let mounted = true

    async function bootstrap() {
      console.debug('[Auth] bootstrap start')
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!mounted) return

        setSession(session)
        if (session?.user?.id) await fetchUserProfile(session.user.id)
        else {
          setUser(null)
          setProfileError('')
          setLoading(false)
        }
      } catch (err) {
        if (!mounted) return
        console.error('[Auth] bootstrap failed:', err)
        setSession(null)
        setUser(null)
        setProfileError('')
        setLoading(false)
      }
    }

    bootstrap()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return

      console.debug('[Auth] onAuthStateChange', event)
      setSession(session)

      if (!session?.user?.id) {
        setUser(null)
        setProfileError('')
        setLoading(false)
        return
      }

      // bootstrap() already handles INITIAL_SESSION; avoid duplicate/competing foreground fetches
      if (event === 'INITIAL_SESSION') return

      // Avoid awaiting Supabase calls directly inside auth-state callback (can deadlock)
      Promise.resolve().then(() => {
        if (!mounted) return
        fetchUserProfile(session.user.id).catch(err => {
          if (!mounted) return
          console.error('[Auth] onAuthStateChange profile refresh failed:', err)
          setSession(null)
          setUser(null)
          setProfileError('')
          setLoading(false)
        })
      })
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!session?.user?.id) return

    const refreshProfile = () => fetchUserProfile(session.user.id, { background: true })
    const onVisible = () => {
      if (!document.hidden) refreshProfile()
    }

    window.addEventListener('focus', refreshProfile)
    document.addEventListener('visibilitychange', onVisible)
    const interval = setInterval(refreshProfile, 60_000)

    return () => {
      window.removeEventListener('focus', refreshProfile)
      document.removeEventListener('visibilitychange', onVisible)
      clearInterval(interval)
    }
  }, [session?.user?.id])

  async function fetchUserProfile(userId, { background = false } = {}) {
    // Background refresh must never invalidate foreground bootstrap/auth loads
    const reqId = background ? profileReqRef.current : ++profileReqRef.current

    if (!background) {
      setLoading(true)
      setProfileError('')
      setUser(null) // prevent stale role UI while refetching
      console.debug('[Auth] foreground profile fetch start', { userId, reqId })
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (!background && reqId !== profileReqRef.current) return

      if (error) throw error

      if (!data) {
        const msg = `No public.users row found for auth user ${userId}`
        console.error(msg)
        setUser(null)
        if (!background) setProfileError(msg)
        return
      }

      const mapped = { ...data, role: normalizeRole(data?.role) }
      setUser(mapped)
      if (!background) {
        setProfileError('')
        console.debug('[Auth] foreground profile fetch success', { userId, reqId, role: mapped.role })
      }
    } catch (err) {
      console.error('Failed to fetch user profile from public.users:', err)
      setUser(null)
      if (!background) setProfileError(err?.message || 'Failed to load user profile')
    } finally {
      if (!background) {
        setLoading(false)
        console.debug('[Auth] foreground profile fetch end', { userId, reqId })
      }
    }
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { data, error }
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  // Source of truth: public.users.role (fetched profile)
  const effectiveRole = normalizeRole(user?.role)

  const isAdmin = effectiveRole === 'admin'
  const isLabor = effectiveRole === 'labor'

  return (
    <AuthContext.Provider value={{ session, user, loading, profileError, effectiveRole, signIn, signOut, isAdmin, isLabor, refreshProfile: () => session?.user?.id ? fetchUserProfile(session.user.id, { background: false }) : null }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
